import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import RequireAuth from "@/components/RequireAuth";
import ErrorBoundary from "@/components/ErrorBoundary";
import { Toaster } from "@/components/ui/sonner";

import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import OverviewPage from "@/pages/OverviewPage";
import ActivityPage from "@/pages/ActivityPage";
import CampaignsPage from "@/pages/CampaignsPage";
import SmsPage from "@/pages/SmsPage";
import CampaignBuilderPage from "@/pages/CampaignBuilderPage";
import CampaignDetailPage from "@/pages/CampaignDetailPage";
import ProspectsPage from "@/pages/ProspectsPage";
import ContactsPage from "@/pages/ContactsPage";
import ActivationFlowPage from "@/pages/ActivationFlowPage";
import ProspectDetailPage from "@/pages/ProspectDetailPage";
import AgentsPage from "@/pages/AgentsPage";
import ContentStudioPage from "@/pages/ContentStudioPage";
import ArticlesPage from "@/pages/ArticlesPage";
import BlogPage from "@/pages/BlogPage";
import LinkedInConnectPage from "@/pages/LinkedInConnectPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import BillingPage from "@/pages/BillingPage";
import SettingsPage from "@/pages/SettingsPage";
import IntegrationsPage from "@/pages/IntegrationsPage";
import HelpPage from "@/pages/HelpPage";
import TeamPage from "@/pages/TeamPage";
import WorkflowsPage from "@/pages/WorkflowsPage";
import PublicScorePage from "@/pages/PublicScorePage";
import InviteAcceptPage from "@/pages/InviteAcceptPage";
import AutopilotPage from "@/pages/AutopilotPage";
import SignalNetworkPage from "@/pages/SignalNetworkPage";
import BudgetPage from "@/pages/BudgetPage";
import SalesCopilotPage from "@/pages/SalesCopilotPage";
import ICPTargetsPage from "@/pages/ICPTargetsPage";
import PipelinePage from "@/pages/PipelinePage";
import ReviewsPage from "@/pages/ReviewsPage";
import M365InboxPage from "@/pages/M365InboxPage";
import LinkedInBrowserPage from "@/pages/LinkedInBrowserPage";
import LinkedInEngagePage from "@/pages/LinkedInEngagePage";
import LeadDiscoveryPage from "@/pages/LeadDiscoveryPage";
import PartnershipsPage from "@/pages/PartnershipsPage";
import MobileApp from "@/pages/mobile/MobileApp";
import MobileHome from "@/pages/mobile/MobileHome";
import MobileEngage from "@/pages/mobile/MobileEngage";
import MobileInbox from "@/pages/mobile/MobileInbox";
import MobileBudget from "@/pages/mobile/MobileBudget";

function InitTheme() {
  useEffect(() => {
    const t = localStorage.getItem("lg_theme") || "dark";
    if (t === "dark") document.documentElement.classList.add("dark");
    else document.documentElement.classList.remove("dark");
  }, []);
  return null;
}

function RootRedirect() {
  const { user } = useAuth();
  return user ? <Navigate to="/app" replace /> : <LandingPage />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <InitTheme />
        <BrowserRouter>
          <ErrorBoundary>
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/invite/:token" element={<InviteAcceptPage />} />
            <Route path="/score" element={<PublicScorePage />} />
            <Route path="/score/:company" element={<PublicScorePage />} />

            <Route path="/app" element={<RequireAuth><OverviewPage /></RequireAuth>} />

            {/* PWA mobile command center — focused human-in-the-loop view */}
            <Route path="/app/m" element={<RequireAuth><MobileApp /></RequireAuth>}>
              <Route index element={<MobileHome />} />
              <Route path="engage" element={<MobileEngage />} />
              <Route path="inbox" element={<MobileInbox />} />
              <Route path="budget" element={<MobileBudget />} />
            </Route>

            <Route path="/app/activity" element={<RequireAuth><ActivityPage /></RequireAuth>} />
            <Route path="/app/campaigns" element={<RequireAuth><CampaignsPage /></RequireAuth>} />
            <Route path="/app/sms" element={<RequireAuth><SmsPage /></RequireAuth>} />
            <Route path="/app/campaigns/new" element={<RequireAuth><CampaignBuilderPage /></RequireAuth>} />
            <Route path="/app/campaigns/:id" element={<RequireAuth><CampaignDetailPage /></RequireAuth>} />
            <Route path="/app/prospects" element={<RequireAuth><ProspectsPage /></RequireAuth>} />
            <Route path="/app/contacts" element={<RequireAuth><ContactsPage /></RequireAuth>} />
            <Route path="/app/activation-flow" element={<RequireAuth><ActivationFlowPage /></RequireAuth>} />
            <Route path="/app/prospects/:id" element={<RequireAuth><ProspectDetailPage /></RequireAuth>} />
            <Route path="/app/agents" element={<RequireAuth><AgentsPage /></RequireAuth>} />
            <Route path="/app/inbox" element={<Navigate to="/app/email" replace />} />
            <Route path="/app/content" element={<RequireAuth><ContentStudioPage /></RequireAuth>} />
            <Route path="/app/articles" element={<RequireAuth><ArticlesPage /></RequireAuth>} />
            <Route path="/app/blog" element={<RequireAuth><BlogPage /></RequireAuth>} />
            <Route path="/app/linkedin-connect" element={<RequireAuth><LinkedInConnectPage /></RequireAuth>} />
            <Route path="/app/analytics" element={<RequireAuth><AnalyticsPage /></RequireAuth>} />
            <Route path="/app/billing" element={<RequireAuth><BillingPage /></RequireAuth>} />
            <Route path="/app/integrations" element={<RequireAuth><IntegrationsPage /></RequireAuth>} />
            <Route path="/app/workflows" element={<RequireAuth><WorkflowsPage /></RequireAuth>} />
            <Route path="/app/autopilot" element={<RequireAuth><AutopilotPage /></RequireAuth>} />
            <Route path="/app/copilot" element={<RequireAuth><SalesCopilotPage /></RequireAuth>} />
            <Route path="/app/icp" element={<RequireAuth><ICPTargetsPage /></RequireAuth>} />
            <Route path="/app/pipeline" element={<RequireAuth><PipelinePage /></RequireAuth>} />
            <Route path="/app/reviews" element={<RequireAuth><ReviewsPage /></RequireAuth>} />
            <Route path="/app/email" element={<RequireAuth><M365InboxPage /></RequireAuth>} />
            <Route path="/app/linkedin" element={<RequireAuth><LinkedInBrowserPage /></RequireAuth>} />
            <Route path="/app/linkedin-engage" element={<RequireAuth><LinkedInEngagePage /></RequireAuth>} />
            <Route path="/app/leads" element={<RequireAuth><LeadDiscoveryPage /></RequireAuth>} />
            <Route path="/app/partnerships" element={<RequireAuth><PartnershipsPage /></RequireAuth>} />
            {/* Obsolete Playwright LinkedIn flow removed — use /app/linkedin (Unipile). Redirect any stale links. */}
            <Route path="/app/linkedin-workers" element={<Navigate to="/app/linkedin" replace />} />
            <Route path="/app/signal-network" element={<RequireAuth><SignalNetworkPage /></RequireAuth>} />
            <Route path="/app/team" element={<RequireAuth><TeamPage /></RequireAuth>} />
            <Route path="/app/budget" element={<RequireAuth><BudgetPage /></RequireAuth>} />
            <Route path="/app/help" element={<RequireAuth><HelpPage /></RequireAuth>} />
            <Route path="/app/settings" element={<RequireAuth><SettingsPage /></RequireAuth>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </ErrorBoundary>
        </BrowserRouter>
        <Toaster position="top-right" />
      </AuthProvider>
    </div>
  );
}

export default App;
